import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Menu,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  Switch,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CloseIcon from "@mui/icons-material/Close";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import {
  listSubTopics,
  listAllSubjects,
  listAllChapters,
  listAllTopics,
  createSubTopic,
  updateSubTopic,
  previewDelete,
  confirmDelete,
  apiErrorMessage,
} from "../../api/assessmentHierarchy";
import useDebouncedValue from "./useDebouncedValue";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
import BulkImportButton from "./BulkImportButton";
const ACCENT = "#17479e";

const joinNames = (names) => (names?.length ? names.join(", ") : "—");

const allColumns = [
  { key: "id", label: "ID", cellCss: { minWidth: "60px", width: "60px" }, getValue: (r) => r.id },
  { key: "title", label: "Name", cellCss: { minWidth: "220px" }, getValue: (r) => r.title },
  { key: "subject", label: "Subject", cellCss: { minWidth: "140px" }, getValue: (r) => joinNames(r.subjectNames) },
  { key: "chapter", label: "Chapter", cellCss: { minWidth: "160px" }, getValue: (r) => joinNames(r.chapterNames) },
  { key: "topic", label: "Topic", cellCss: { minWidth: "180px" }, getValue: (r) => joinNames(r.topicNames) },
  { key: "questions", label: "Questions", cellCss: { align: "center", minWidth: "110px" }, getValue: (r) => r.questionCount },
  { key: "status", label: "Status", cellCss: { align: "center", minWidth: "120px" }, getValue: (r) => r.status },
];

const defaultVisibleKeys = new Set(allColumns.map((c) => c.key));
const STORAGE_KEY = "assessmentSubTopicsVisibleColumns";

const statusCfg = {
  ACTIVE: { label: "Active", color: "#2e7d32", bg: "#e8f5e9" },
  INACTIVE: { label: "Inactive", color: "#555", bg: "#f0f0f0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

function SubTopicFormDialog({ open, subTopic, subjects, chapters, topics, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [subjectIds, setSubjectIds] = useState([]);
  const [chapterIds, setChapterIds] = useState([]);
  const [topicIds, setTopicIds] = useState([]);
  const [order, setOrder] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setNameTouched(false);
    setTitle(subTopic?.title || "");
    setDescription(subTopic?.description || "");
    setSubjectIds(subTopic?.subjectIds || []);
    setChapterIds(subTopic?.chapterIds || []);
    setTopicIds(subTopic?.topicIds || []);
    setOrder("");
    setStatus(subTopic?.status || "ACTIVE");
  }, [open, subTopic]);

  const chapterOptions = useMemo(
    () => chapters.filter((c) => c.subjectIds?.some((id) => subjectIds.includes(id))),
    [chapters, subjectIds],
  );
  const topicOptions = useMemo(
    () => topics.filter((t) => t.chapterIds?.some((id) => chapterIds.includes(id))),
    [topics, chapterIds],
  );

  // Narrowing a parent level drops any selected children that no longer belong to it.
  const handleSubjectChange = (value) => {
    setSubjectIds(value);
    const nextChapters = chapters
      .filter((c) => chapterIds.includes(c.id) && c.subjectIds?.some((id) => value.includes(id)))
      .map((c) => c.id);
    setChapterIds(nextChapters);
    setTopicIds(
      topics
        .filter((t) => topicIds.includes(t.id) && t.chapterIds?.some((id) => nextChapters.includes(id)))
        .map((t) => t.id),
    );
  };

  const handleChapterChange = (value) => {
    setChapterIds(value);
    setTopicIds(
      topics
        .filter((t) => topicIds.includes(t.id) && t.chapterIds?.some((id) => value.includes(id)))
        .map((t) => t.id),
    );
  };

  const canSubmit =
    title.trim() && subjectIds.length > 0 && chapterIds.length > 0 && topicIds.length > 0;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        id: subTopic?.id,
        name: title.trim(),
        description: description.trim(),
        subjectIds,
        chapterIds,
        topicIds,
        ...(order !== "" ? { displayOrder: Number(order) } : {}),
        active: status === "ACTIVE",
      });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to save sub-topic"));
    } finally {
      setSubmitting(false);
    }
  };

  const renderMulti = (options, labelOf) => (selected) =>
    selected.map((id) => labelOf(options.find((o) => o.id === id)) ?? id).join(", ");

  const isActive = status === "ACTIVE";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 2.5,
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          >
            <LabelOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>
              {subTopic ? "Edit Sub-Topic" : "Add Sub-Topic"}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
              {subTopic ? "Update name, mapping and status" : "Name it, map it and set status"}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <DialogContent sx={{ pt: 3, backgroundColor: "#f7f8fb" }}>
        <Card sx={{ boxShadow: 1, borderRadius: 3, overflow: "hidden" }}>
          <CardContent sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.25 }}>
            <TextField
              label="Sub-Topic Name"
              fullWidth
              size="small"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => setNameTouched(true)}
              error={nameTouched && !title.trim()}
              helperText={nameTouched && !title.trim() ? "Please fill out this field" : undefined}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <FormControl fullWidth size="small" required>
              <InputLabel>Subjects</InputLabel>
              <Select
                multiple
                label="Subjects *"
                value={subjectIds}
                onChange={(e) => handleSubjectChange(e.target.value)}
                renderValue={renderMulti(subjects, (s) => s?.name)}
              >
                {subjects.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    <Checkbox checked={subjectIds.includes(s.id)} size="small" />
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={subjectIds.length === 0} required>
              <InputLabel>Chapters</InputLabel>
              <Select
                multiple
                label="Chapters *"
                value={chapterIds}
                onChange={(e) => handleChapterChange(e.target.value)}
                renderValue={renderMulti(chapters, (c) => c?.title)}
              >
                {chapterOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    <Checkbox checked={chapterIds.includes(c.id)} size="small" />
                    {c.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={chapterIds.length === 0} required>
              <InputLabel>Topics</InputLabel>
              <Select
                multiple
                label="Topics *"
                value={topicIds}
                onChange={(e) => setTopicIds(e.target.value)}
                renderValue={renderMulti(topics, (t) => t?.title)}
              >
                {topicOptions.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    <Checkbox checked={topicIds.includes(t.id)} size="small" />
                    {t.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Display Order (optional)"
              type="number"
              fullWidth
              size="small"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                border: "1px solid",
                borderColor: isActive ? "#c8e6c9" : "#e0e0e0",
                backgroundColor: isActive ? "#f1f8f2" : "#fafafa",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                  {isActive ? "Active" : "Inactive"}
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
                  {isActive ? "Visible and available to students" : "Hidden from students"}
                </Typography>
              </Box>
              <Switch
                checked={isActive}
                onChange={(e) => setStatus(e.target.checked ? "ACTIVE" : "INACTIVE")}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2e7d32" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#2e7d32" },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              flex: "0 0 auto",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: "#555",
              backgroundColor: "#eceff1",
              "&:hover": { backgroundColor: "#e0e3e6" },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={submitting}
            onClick={handleSubmit}
            disabled={!canSubmit}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
              "&:hover": { background: `linear-gradient(135deg, #123a80 0%, #0d47a1 100%)` },
              "&.Mui-disabled": { background: "#b0bec5" },
            }}
          >
            {subTopic ? "Save Changes" : "Create Sub-Topic"}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function AssessmentSubTopics() {
  const [subTopics, setSubTopics] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [subjectFilter, setSubjectFilter] = useState("");
  const [chapterFilter, setChapterFilter] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orderBy, setOrderBy] = useState("title");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, message }

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) return new Set(filtered);
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  const loadSubTopics = () => {
    setLoading(true);
    listSubTopics({
      page,
      size: rowsPerPage,
      search: debouncedSearch,
      subjectId: subjectFilter,
      chapterId: chapterFilter,
      topicId: topicFilter,
      isActive: statusFilter ? statusFilter === "ACTIVE" : undefined,
    })
      .then((data) => {
        setSubTopics(data.content);
        setTotalElements(data.totalElements ?? 0);
      })
      .catch((err) => {
        setSubTopics([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load sub-topics"));
      })
      .finally(() => setLoading(false));
  };

  // Hierarchy options for the filter bar and the form dialog.
  const loadHierarchy = () => {
    Promise.all([listAllSubjects(), listAllChapters(), listAllTopics()])
      .then(([subjectRows, chapterRows, topicRows]) => {
        setSubjects(subjectRows);
        setChapters(chapterRows);
        setTopics(topicRows);
      })
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load subjects/chapters/topics")));
  };

  useEffect(() => {
    loadHierarchy();
  }, []);

  useEffect(() => {
    loadSubTopics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, subjectFilter, chapterFilter, topicFilter, statusFilter]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(visibleColumns)));
    } catch {}
  }, [visibleColumns]);

  const chapterFilterOptions = useMemo(
    () => chapters.filter((c) => !subjectFilter || c.subjectIds?.includes(subjectFilter)),
    [chapters, subjectFilter],
  );
  const topicFilterOptions = useMemo(
    () =>
      topics.filter((t) => {
        if (chapterFilter) return t.chapterIds?.includes(chapterFilter);
        if (subjectFilter) return t.subjectIds?.includes(subjectFilter);
        return true;
      }),
    [topics, chapterFilter, subjectFilter],
  );

  const handleSubjectFilterChange = (value) => {
    setSubjectFilter(value);
    setChapterFilter("");
    setTopicFilter("");
  };
  const handleChapterFilterChange = (value) => {
    setChapterFilter(value);
    setTopicFilter("");
  };

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  // The API returns one server-side page; sorting applies within that page.
  const sortedData = useMemo(() => {
    const fieldFor = (row, key) => {
      if (key === "subject") return joinNames(row.subjectNames);
      if (key === "chapter") return joinNames(row.chapterNames);
      if (key === "topic") return joinNames(row.topicNames);
      return row[key];
    };
    return [...subTopics].sort((a, b) => {
      const aValue = fieldFor(a, orderBy);
      const bValue = fieldFor(b, orderBy);
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [subTopics, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, subjectFilter, chapterFilter, topicFilter, statusFilter]);

  const paginatedData = sortedData;

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (subTopic) => {
    setEditing(subTopic);
    setFormOpen(true);
  };

  const handleSave = async (values) => {
    const { id, ...payload } = values;
    if (id) await updateSubTopic(id, payload);
    else await createSubTopic(payload);
    toast.success(id ? "Sub-Topic updated successfully!" : "Sub-Topic created successfully!");
    setFormOpen(false);
    loadSubTopics();
  };

  // Step 1 of the two-step delete: ask the backend what would be removed. A
  // guard (e.g. still referenced by questions) rejects here, so no dialog opens.
  const handleDeleteClick = (row) => {
    previewDelete("subtopic", row.id)
      .then((preview) => setDeleteTarget({ id: row.id, message: preview?.message }))
      .catch((err) => toast.error(apiErrorMessage(err, "Sub-Topic cannot be deleted")));
  };

  const handleDelete = () => {
    const { id } = deleteTarget;
    confirmDelete("subtopic", id)
      .then(() => {
        toast.success("Sub-Topic deleted!");
        setDeleteTarget(null);
        if (subTopics.length === 1 && page > 0) setPage(page - 1);
        else loadSubTopics();
      })
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to delete sub-topic")));
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: "1 1 720px", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search sub-topics"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Subject</InputLabel>
                <Select label="Subject" value={subjectFilter} onChange={(e) => handleSubjectFilterChange(e.target.value)}>
                  <MenuItem value="">All subjects</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Chapter</InputLabel>
                <Select label="Chapter" value={chapterFilter} onChange={(e) => handleChapterFilterChange(e.target.value)}>
                  <MenuItem value="">All chapters</MenuItem>
                  {chapterFilterOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>Topic</InputLabel>
                <Select label="Topic" value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)}>
                  <MenuItem value="">All topics</MenuItem>
                  {topicFilterOptions.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{ sx: { width: 200, maxHeight: 500, overflowY: "auto", borderRadius: 2 } }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={<Checkbox checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} />}
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <BulkImportButton
                onImported={() => {
                  loadSubTopics();
                  loadHierarchy();
                }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Add Sub-Topic
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2, borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)", minHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    sortDirection={orderBy === c.key ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === c.key}
                      direction={orderBy === c.key ? order : "asc"}
                      onClick={() => handleSort(c.key)}
                      sx={{
                        color: "#ffffff !important",
                        "&.Mui-active": { color: "#ffffff !important" },
                        "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                      }}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: "110px",
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 1} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No sub-topics found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.cellCss?.align} sx={{ padding: "10px 12px", ...c.cellCss }}>
                        {(() => {
                          if (c.key === "id") {
                            return (
                              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{row.id}</Typography>
                            );
                          }
                          if (c.key === "title") {
                            return <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{row.title}</Typography>;
                          }
                          if (c.key === "status") return <StatusChip value={row.status} />;
                          return c.getValue(row);
                        })()}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ padding: "10px 12px", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit Sub-Topic" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEdit(row)}
                            sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Sub-Topic" arrow placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(row)}
                            sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>

      <SubTopicFormDialog
        open={formOpen}
        subTopic={editing}
        subjects={subjects}
        chapters={chapters}
        topics={topics}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message={deleteTarget?.message || "This action cannot be undone."}
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default AssessmentSubTopics;
